/* Evenly — theme.js
   Vanilla, no dependencies. Loaded with `defer`. */
(() => {
  'use strict';

  const E = window.Evenly || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------------- Analytics ----------------
     Publishes custom events to Shopify Customer Events (Web Pixels)
     and mirrors them to window.dataLayer if one exists.
     Standard events (product_viewed, search_submitted, product_added_to_cart,
     checkout_started, checkout_completed) are emitted by Shopify itself. */
  const track = (name, data = {}) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data);
      }
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...data });
    } catch (e) { /* never break the page for analytics */ }
  };
  E.track = track;

  document.addEventListener('click', (ev) => {
    const el = ev.target.closest('[data-track]');
    if (!el) return;
    track(el.dataset.track, { label: el.dataset.trackLabel || el.textContent.trim().slice(0, 60), location: el.dataset.trackLocation || '' });
  });

  /* ---------------- Money ---------------- */
  const money = (cents) => {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: E.currency || 'GBP' }).format(cents / 100).replace(/\.00$/, '');
    } catch (e) { return '£' + (cents / 100).toFixed(2); }
  };
  E.money = money;

  /* ---------------- Toast ---------------- */
  let toastTimer;
  const toast = (msg) => {
    const t = $('[data-toast]');
    if (!t) return;
    t.textContent = msg;
    t.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { t.hidden = true; }, 3200);
  };

  /* ---------------- Reveal on scroll ---------------- */
  const reveal = () => {
    const els = $$('[data-reveal]:not(.is-visible)');
    if (!document.body.classList.contains('has-motion') || !('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  };

  /* One-off "seen" events (e.g. shade guide viewed) */
  const trackViews = () => {
    const els = $$('[data-track-view]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { track(en.target.dataset.trackView, { path: location.pathname }); io.unobserve(en.target); }
      });
    }, { threshold: 0.35 });
    els.forEach((el) => io.observe(el));
  };

  /* ---------------- Header ---------------- */
  const header = () => {
    const wrap = $('[data-header]');
    if (!wrap) return;
    const onScroll = () => wrap.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* ---------------- Dialogs (menu, cart, zoom) ---------------- */
  let lastFocus = null;
  const openDialog = (dlg) => {
    if (!dlg || dlg.open) return;
    lastFocus = document.activeElement;
    dlg.showModal();
    document.body.classList.add('is-locked');
    document.querySelectorAll(`[aria-controls="${dlg.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
  };
  const closeDialog = (dlg) => {
    if (!dlg || !dlg.open) return;
    dlg.close();
  };
  document.addEventListener('click', (ev) => {
    const opener = ev.target.closest('[data-open-dialog]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.openDialog);
      if (dlg) { ev.preventDefault(); openDialog(dlg); }
      return;
    }
    const closer = ev.target.closest('[data-close-dialog]');
    if (closer) { closeDialog(closer.closest('dialog')); return; }
    // click on backdrop
    if (ev.target.tagName === 'DIALOG' && ev.target.open) {
      const r = ev.target.getBoundingClientRect();
      const inside = ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
      if (!inside) closeDialog(ev.target);
    }
  });
  document.addEventListener('close', (ev) => {
    if (ev.target.tagName !== 'DIALOG') return;
    document.body.classList.remove('is-locked');
    document.querySelectorAll(`[aria-controls="${ev.target.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  }, true);

  /* ---------------- Cart ---------------- */
  const cartDrawer = () => document.getElementById('CartDrawer');
  const isCartPage = () => document.body.classList.contains('template-cart');

  const postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || E.strings.error);
      err.data = data;
      throw err;
    }
    return data;
  };

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    $$('[data-cart-label]').forEach((el) => el.setAttribute('aria-label', `Bag, ${count} item${count === 1 ? '' : 's'}`));
  };

  const renderDrawer = (sectionsHTML) => {
    const drawer = cartDrawer();
    if (!drawer) return;
    const html = sectionsHTML && sectionsHTML['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-drawer-inner]', doc);
    const current = $('[data-cart-drawer-inner]', drawer);
    if (fresh && current) current.replaceWith(fresh);
  };

  const refreshCart = async (sectionsHTML) => {
    if (isCartPage()) { window.location.reload(); return; }
    let html = sectionsHTML;
    if (!html) {
      const res = await fetch(`${E.routes.root}?sections=cart-drawer`);
      html = await res.json();
    }
    renderDrawer(html);
    const cart = await (await fetch(`${E.routes.cart}.js`)).json();
    updateCount(cart.item_count);
    return cart;
  };

  const addToCart = async (items, button) => {
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
    try {
      const wantsDrawer = E.cartType === 'drawer' && cartDrawer();
      const data = await postJSON(E.routes.cartAdd, {
        items,
        sections: wantsDrawer ? 'cart-drawer' : undefined,
        sections_url: window.location.pathname
      });
      if (wantsDrawer) {
        renderDrawer(data.sections);
        const cart = await (await fetch(`${E.routes.cart}.js`)).json();
        updateCount(cart.item_count);
        openDialog(cartDrawer());
      } else {
        window.location.href = E.routes.cart;
      }
      return data;
    } catch (err) {
      toast(err.message || E.strings.error);
      throw err;
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
    }
  };
  E.addToCart = addToCart;

  // Quick add (collection cards, cart upsell)
  document.addEventListener('click', (ev) => {
    const btn = ev.target.closest('[data-quick-add]');
    if (!btn) return;
    ev.preventDefault();
    addToCart([{ id: Number(btn.dataset.quickAdd), quantity: 1 }], btn).catch(() => {});
    track('evenly_quick_add', { variant_id: btn.dataset.quickAdd, product: btn.dataset.productTitle || '' });
  });

  // Product forms
  document.addEventListener('submit', (ev) => {
    const form = ev.target.closest('form[data-product-form]');
    if (!form || !window.fetch) return;
    ev.preventDefault();
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    const quantity = Math.max(1, Number(fd.get('quantity') || 1));
    if (!id) return;
    const btn = form.querySelector('[type="submit"]') || document.querySelector(`[form="${form.id}"][type="submit"]`);
    const submitter = ev.submitter || btn;
    addToCart([{ id, quantity }], submitter).catch(() => {});
  });

  // Cart line changes (drawer + page)
  const changeLine = async (key, quantity, control) => {
    const scope = control.closest('[data-cart-item]');
    if (scope) scope.style.opacity = '.5';
    try {
      const data = await postJSON(E.routes.cartChange, { id: key, quantity, sections: 'cart-drawer', sections_url: window.location.pathname });
      if (isCartPage()) { window.location.reload(); return; }
      renderDrawer(data.sections);
      updateCount(data.item_count);
      const focusTarget = $(`[data-cart-item="${CSS.escape(key)}"] [data-qty-input]`) || $('[data-cart-drawer-inner] .drawer__title') || $('[data-cart-drawer-inner]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    } catch (err) {
      toast(err.message || E.strings.error);
      if (scope) scope.style.opacity = '';
    }
  };

  document.addEventListener('click', (ev) => {
    const rm = ev.target.closest('[data-cart-remove]');
    if (rm) { ev.preventDefault(); changeLine(rm.dataset.cartRemove, 0, rm); return; }
    const step = ev.target.closest('[data-cart-step]');
    if (step) {
      ev.preventDefault();
      const item = step.closest('[data-cart-item]');
      const input = $('[data-qty-input]', item);
      const next = Math.max(0, Number(input.value) + Number(step.dataset.cartStep));
      changeLine(item.dataset.cartItem, next, step);
    }
  });
  document.addEventListener('change', (ev) => {
    const input = ev.target.closest('[data-cart-item] [data-qty-input]');
    if (!input) return;
    const item = input.closest('[data-cart-item]');
    changeLine(item.dataset.cartItem, Math.max(0, Number(input.value) || 0), input);
  });

  // Swap singles for The Pair
  document.addEventListener('click', async (ev) => {
    const btn = ev.target.closest('[data-pair-swap]');
    if (!btn) return;
    ev.preventDefault();
    btn.classList.add('is-loading');
    try {
      const reduce = JSON.parse(btn.dataset.reduce || '{}'); // { lineKey: newQty }
      if (Object.keys(reduce).length) await postJSON(E.routes.cartUpdate, { updates: reduce });
      track('evenly_cart_pair_swap', { variant_id: btn.dataset.pairSwap });
      await addToCart([{ id: Number(btn.dataset.pairSwap), quantity: 1 }], null);
      toast('Swapped to The Pair. You’re saving £8.');
    } catch (e) {
      toast(E.strings.error);
      refreshCart().catch(() => {});
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------------- Quantity steppers (product page) ---------------- */
  document.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-qty-step]');
    if (!b) return;
    const input = b.parentElement.querySelector('input');
    const v = Math.max(1, Math.min(99, (Number(input.value) || 1) + Number(b.dataset.qtyStep)));
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------- Product: purchase option + shade ---------------- */
  const initProduct = (root) => {
    const dataEl = $('[data-product-json]', root);
    if (!dataEl) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const form = $('form[data-product-form]', root);
    const idInput = $('input[name="id"]', form);
    const submit = $$('[data-atc]', root.ownerDocument || document).filter((b) => b.closest('[data-product-root]') === root || b.getAttribute('form') === form.id);
    const shadeWrap = $('[data-shade-wrap]', root);
    const shadeInputs = $$('input[data-shade]', root);
    const priceEl = $('[data-product-price]', root);
    const stickyMeta = $('[data-sticky-meta]');
    const stickyTitle = $('[data-sticky-title]');
    const shadeLabel = $('[data-shade-selected]', root);

    const currentOption = () => {
      const checked = $('input[data-buy-option]:checked', root);
      const key = checked ? checked.value : data.default;
      return data.options.find((o) => o.key === key) || data.options[0];
    };
    const currentShade = () => {
      const checked = shadeInputs.find((i) => i.checked);
      return checked ? checked.value : null;
    };

    const update = (source) => {
      const opt = currentOption();
      const hasShades = opt.variants.some((v) => v.shade);
      if (shadeWrap) shadeWrap.hidden = !hasShades;
      let shade = currentShade();
      shadeInputs.forEach((i) => {
        const match = opt.variants.find((v) => v.shade === i.value);
        i.disabled = !match || !match.available;
      });
      let variant = hasShades ? opt.variants.find((v) => v.shade === shade) : opt.variants[0];
      if (!variant) variant = opt.variants.find((v) => v.available) || opt.variants[0];
      if (!variant) return;
      if (hasShades && variant.shade && shade !== variant.shade) {
        const inp = shadeInputs.find((i) => i.value === variant.shade);
        if (inp) inp.checked = true;
        shade = variant.shade;
      }
      idInput.value = variant.id;
      if (shadeLabel) shadeLabel.textContent = shade ? `: ${shade}` : '';

      const label = variant.available ? `${E.strings.addToCart} · ${money(variant.price)}` : E.strings.soldOut;
      submit.forEach((b) => {
        b.disabled = !variant.available;
        const l = b.querySelector('[data-atc-label]');
        if (l) l.textContent = label;
      });
      if (priceEl) {
        priceEl.innerHTML = `<span class="price__current">${money(variant.price)}</span>` +
          (variant.compare > variant.price
            ? ` <s class="price__compare"><span class="visually-hidden">Worth</span>${money(variant.compare)}</s> <span class="price__save">Save ${money(variant.compare - variant.price)}</span>`
            : '');
      }
      if (stickyTitle) stickyTitle.textContent = opt.label;
      if (stickyMeta) stickyMeta.textContent = [shade, money(variant.price)].filter(Boolean).join(' · ');

      if (source === 'option') track('evenly_purchase_option_selected', { option: opt.key, variant_id: variant.id, product: data.product });
      if (source === 'shade') track('evenly_shade_selected', { shade, option: opt.key, variant_id: variant.id, product: data.product });
    };

    root.addEventListener('change', (ev) => {
      if (ev.target.matches('input[data-buy-option]')) update('option');
      else if (ev.target.matches('input[data-shade]')) update('shade');
    });

    // Shade guide "choose this shade" buttons
    document.addEventListener('click', (ev) => {
      const b = ev.target.closest('[data-pick-shade]');
      if (!b) return;
      const inp = shadeInputs.find((i) => i.value === b.dataset.pickShade);
      if (!inp) return;
      ev.preventDefault();
      inp.checked = true;
      update('shade');
      const target = shadeWrap || form;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => inp.focus({ preventScroll: true }), 400);
    });

    update();

    // Sticky add-to-cart
    const sticky = $('[data-sticky-atc]');
    const mainBtn = $('[data-atc-main]', root);
    if (sticky && mainBtn) {
      // Scroll-position check (IntersectionObserver misses fast jumps past the button).
      let ticking = false;
      let shown = null;
      const check = () => {
        ticking = false;
        const past = mainBtn.getBoundingClientRect().bottom < 0;
        if (past === shown) return;
        shown = past;
        sticky.classList.toggle('is-visible', past);
        sticky.setAttribute('aria-hidden', String(!past));
        $$('button, a', sticky).forEach((el) => { el.tabIndex = past ? 0 : -1; });
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } }, { passive: true });
      check();
    }
  };

  /* ---------------- Gallery ---------------- */
  const initGallery = (root) => {
    const track = $('[data-gallery-track]', root);
    const dots = $$('[data-gallery-dot]', root);
    if (!track) return;
    if (dots.length) {
      dots.forEach((d, i) => d.addEventListener('click', () => {
        track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
      }));
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          dots.forEach((d, j) => d.setAttribute('aria-current', String(i === j)));
        });
      }, { passive: true });
    }
    const zoom = $('[data-zoom-dialog]', root);
    if (zoom) {
      root.addEventListener('click', (ev) => {
        const b = ev.target.closest('[data-zoom-index]');
        if (!b) return;
        const imgs = $$('img[data-src]', zoom);
        imgs.forEach((img) => { if (!img.src) img.src = img.dataset.src; });
        openDialog(zoom);
        const target = imgs[Number(b.dataset.zoomIndex)];
        if (target) setTimeout(() => target.scrollIntoView({ block: 'start' }), 30);
      });
    }
  };

  /* ---------------- FAQ tracking ---------------- */
  document.addEventListener('toggle', (ev) => {
    const d = ev.target;
    if (d.matches && d.matches('details[data-faq]') && d.open) {
      const s = d.querySelector('summary');
      track('evenly_faq_opened', { question: s ? s.textContent.trim().slice(0, 80) : '' });
    }
  }, true);

  /* ---------------- Filters panel ---------------- */
  document.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-close-details]');
    if (!b) return;
    const d = b.closest('details');
    if (d) { d.open = false; const s = d.querySelector('summary'); if (s) s.focus(); }
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    const d = document.querySelector('details.filters[open]');
    if (d) { d.open = false; const s = d.querySelector('summary'); if (s) s.focus(); }
  });

  /* ---------------- Collection sort ---------------- */
  document.addEventListener('change', (ev) => {
    const s = ev.target.closest('[data-sort]');
    if (!s) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', s.value);
    window.location.href = url.toString();
  });

  /* ---------------- Newsletter ---------------- */
  document.addEventListener('submit', (ev) => {
    const f = ev.target.closest('form[data-signup-form]');
    if (!f) return;
    const sms = f.querySelector('[name="contact[sms_opt_in]"]');
    track('evenly_email_signup_submitted', { location: f.dataset.signupForm, sms_opt_in: sms ? sms.checked : false });
  });

  /* ---------------- Init ---------------- */
  const init = () => {
    header();
    reveal();
    trackViews();
    $$('[data-product-root]').forEach(initProduct);
    $$('[data-gallery]').forEach(initGallery);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor support
  document.addEventListener('shopify:section:load', (ev) => {
    reveal();
    $$('[data-product-root]', ev.target).forEach(initProduct);
    $$('[data-gallery]', ev.target).forEach(initGallery);
  });
})();
